import React, {
  createContext,
  useState,
  useCallback,
  useRef,
  useContext,
} from 'react';
import {
  ProvideragendContasContextProps,
  agendContasContextData,
  tipoContaProps,
} from '../types';
import { useForm } from 'react-hook-form';
import { schema } from '../validation';
import { yupResolver } from '@hookform/resolvers/yup';
import { addDays, addWeeks, addMonths, addYears } from 'date-fns';
import moment from 'moment';
import { LojaContext } from '~/context/loja';

export const agendContasContext = createContext({} as agendContasContextData);

export const AgendContasContext: React.FC<ProvideragendContasContextProps> = ({
  children,
}) => {
  const [isUpdate, setIsUpdate] = useState<boolean>(false);
  const [tipoConta, setTipoConta] = useState<tipoContaProps>({
    value: 0,
    label: 'pgto',
  });
  const [tipoCategoria, setTipoCategoria] = useState<string>('Despesa');
  const [lojaRetriever, setLojaRetriever] = useState<number | null>(null);
  const [contaBancoRetriever, setContaBancoRetriever] = useState<number | null>(
    null,
  );
  const [disabledQtdParcela, setDisabledQtdParcela] = useState<boolean>(false);
  const [disabledDtaTermino, setDisabledDtaTermino] = useState<boolean>(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { loja } = useContext(LojaContext);

  const {
    register,
    reset,
    setValue,
    handleSubmit,
    watch,
    clearErrors,
    getValues,
    control,
    setFocus,
    setError,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    reValidateMode: 'onSubmit',
  });

  const handleChangeTipoConta = useCallback((tipo: tipoContaProps) => {
    setTipoConta(tipo);
    if (tipo.value === 0) {
      return setTipoCategoria('Despesa');
    }
    setTipoCategoria('Receita');
  }, []);

  const handleDisableQtdParcelas = useCallback(
    (disabled: boolean) => {
      setDisabledQtdParcela(disabled);
      if (disabled === true) {
        setValue('qtd_parcelas', 1);
      }
    },
    [setValue],
  );

  const handleDisabledDtaTermino = useCallback(
    (disabled: boolean) => {
      setDisabledDtaTermino(disabled);
      if (disabled === true) {
        setValue('dta_termino', '');
      }
    },
    [setValue],
  );

  const handleChangeDtaTermino = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      const { tipo_frequencia, qtd_parcelas, dta_docto } = getValues();

      if (!tipo_frequencia) return;
      if (!dta_docto) return;
      if (!qtd_parcelas) return;
      if (Number(qtd_parcelas) === 0) return;

      const parc = -1;
      const total = Number(qtd_parcelas) + parc;
      const labelTipoFreq = tipo_frequencia?.label.toUpperCase();

      let date: Date = moment(dta_docto, 'YYYY-MM-DD').toDate();

      switch (labelTipoFreq) {
        case 'DIÁRIA':
          date = addDays(date, total);
          return setValue('dta_termino', moment(date).format('YYYY-MM-DD'));
        case 'SEMANAL':
          date = addWeeks(date, total);
          return setValue('dta_termino', moment(date).format('YYYY-MM-DD'));
        case 'MENSAL':
          date = addMonths(date, total);
          return setValue('dta_termino', moment(date).format('YYYY-MM-DD'));
        case 'TRIMESTRAL':
          date = addMonths(date, total * 3);
          return setValue('dta_termino', moment(date).format('YYYY-MM-DD'));
        case 'SEMESTRAL':
          date = addMonths(date, total * 6);
          return setValue('dta_termino', moment(date).format('YYYY-MM-DD'));
        case 'ANUAL':
          date = addYears(date, total);
          return setValue('dta_termino', moment(date).format('YYYY-MM-DD'));
        default:
          setValue('qtd_parcelas', 1);
          return setValue(
            'dta_termino',
            moment(dta_docto).format('YYYY-MM-DD'),
          );
      }
    }, 500);
  };

  return (
    <agendContasContext.Provider
      value={{
        isUpdate,
        setIsUpdate,
        tipoConta,
        handleChangeTipoConta,
        contaBancoRetriever,
        setContaBancoRetriever,
        lojaRetriever,
        setLojaRetriever,
        tipoCategoria,
        handleChangeDtaTermino,
        loja,
        disabledQtdParcela,
        handleDisableQtdParcelas,
        handleDisabledDtaTermino,
        disabledDtaTermino,
        formAgendamento: {
          register,
          reset,
          setValue,
          handleSubmit,
          watch,
          clearErrors,
          getValues,
          control,
          setFocus,
          setError,
          errors,
        },
      }}
    >
      {children}
    </agendContasContext.Provider>
  );
};
